// CMS Alerts Dashboard
import React, { useState, useEffect } from 'react';
import { useCMS } from './CMSContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Bell, CheckCircle, AlertTriangle, XCircle, Clock, Eye } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ALERT_TYPES = {
  fraud_detected: { label: 'Fraud Detected', icon: AlertTriangle, color: 'text-red-400' },
  high_value_refund: { label: 'High Value Refund', icon: XCircle, color: 'text-orange-400' },
  pass_abuse: { label: 'Pass Abuse', icon: AlertTriangle, color: 'text-yellow-400' },
  suspicious_activity: { label: 'Suspicious Activity', icon: Eye, color: 'text-orange-400' },
  system_warning: { label: 'System Warning', icon: Bell, color: 'text-blue-400' },
  daily_summary: { label: 'Daily Summary', icon: Clock, color: 'text-slate-400' }
};

const STATUSES = {
  new: { label: 'New', color: 'bg-red-500' },
  acknowledged: { label: 'Acknowledged', color: 'bg-yellow-500' },
  resolved: { label: 'Resolved', color: 'bg-green-500' },
  dismissed: { label: 'Dismissed', color: 'bg-slate-500' }
};

const SEVERITIES = {
  low: { label: 'Low', color: 'bg-blue-500' },
  medium: { label: 'Medium', color: 'bg-yellow-500' },
  high: { label: 'High', color: 'bg-orange-500' },
  critical: { label: 'Critical', color: 'bg-red-500' }
};

const CMSAlerts = () => {
  const { token } = useCMS();
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newCount, setNewCount] = useState(0);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [severityFilter, setSeverityFilter] = useState('');

  useEffect(() => {
    fetchAlerts();
  }, [page, statusFilter, severityFilter]);

  const fetchAlerts = async () => {
    try {
      let url = `${API}/cms/alerts?page=${page}&limit=20`;
      if (statusFilter) url += `&status=${statusFilter}`;
      if (severityFilter) url += `&severity=${severityFilter}`;
      
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setAlerts(data.alerts || []);
      setNewCount(data.new_count || 0);
      setTotal(data.total || 0);
    } catch (error) {
      toast.error('Failed to load alerts');
    } finally {
      setLoading(false);
    }
  };

  const handleAcknowledge = async (alertId) => {
    try {
      const res = await fetch(`${API}/cms/alerts/${alertId}/acknowledge`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Alert acknowledged');
        fetchAlerts();
      } else {
        toast.error('Failed to acknowledge alert');
      }
    } catch (error) {
      toast.error('Failed to acknowledge alert');
    }
  };

  const handleUpdateStatus = async (alertId, status, note = '') => {
    try {
      const res = await fetch(`${API}/cms/alerts/${alertId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ status, resolution_note: note })
      });

      if (res.ok) {
        toast.success(`Alert ${status}`);
        fetchAlerts();
      } else {
        toast.error('Failed to update alert');
      }
    } catch (error) {
      toast.error('Failed to update alert');
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString();
  };

  const AlertIcon = ({ type }) => {
    const config = ALERT_TYPES[type] || ALERT_TYPES.system_warning;
    const Icon = config.icon;
    return <Icon className={`h-5 w-5 ${config.color}`} />;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cms-alerts">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Bell className="h-6 w-6" /> Alerts
            {newCount > 0 && (
              <Badge className="bg-red-500 text-white">{newCount} new</Badge>
            )}
          </h1>
          <p className="text-slate-400">Monitor system alerts and notifications</p>
        </div>
      </div>

      {/* Filters */}
      <Card className="bg-slate-800 border-slate-700">
        <CardContent className="p-4">
          <div className="flex gap-4">
            <Select value={statusFilter || "all"} onValueChange={(v) => { setStatusFilter(v === "all" ? "" : v); setPage(1); }}>
              <SelectTrigger className="w-40 bg-slate-900 border-slate-600">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="new">New</SelectItem>
                <SelectItem value="acknowledged">Acknowledged</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="dismissed">Dismissed</SelectItem>
              </SelectContent>
            </Select>
            <Select value={severityFilter || "all"} onValueChange={(v) => { setSeverityFilter(v === "all" ? "" : v); setPage(1); }}>
              <SelectTrigger className="w-40 bg-slate-900 border-slate-600">
                <SelectValue placeholder="Severity" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Severity</SelectItem>
                <SelectItem value="low">Low</SelectItem>
                <SelectItem value="medium">Medium</SelectItem>
                <SelectItem value="high">High</SelectItem>
                <SelectItem value="critical">Critical</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-sm text-slate-400 flex items-center ml-auto">
              Total: {total} alerts
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Alerts List */}
      {alerts.length === 0 ? (
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="py-12 text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
            <p className="text-slate-400">No alerts to display</p>
            <p className="text-sm text-slate-500">All systems operating normally</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {alerts.map(alert => (
            <Card key={alert.alert_id} className={`bg-slate-800 border-slate-700 ${alert.status === 'new' ? 'border-l-4 border-l-red-500' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <AlertIcon type={alert.alert_type} />
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-white">{alert.title}</h3>
                        <Badge className={`${STATUSES[alert.status]?.color || 'bg-slate-500'} text-white text-xs`}>
                          {STATUSES[alert.status]?.label || alert.status}
                        </Badge>
                        <Badge className={`${SEVERITIES[alert.severity]?.color || 'bg-slate-500'} text-white text-xs`}>
                          {alert.severity}
                        </Badge>
                      </div>
                      <p className="text-sm text-slate-400">{alert.message}</p>
                      <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                        <span>{formatDate(alert.created_at)}</span>
                        {alert.entity_type && <span>{alert.entity_type}: {alert.entity_id}</span>}
                      </div>
                      {alert.resolution_note && (
                        <p className="text-sm text-green-400 mt-2">Resolution: {alert.resolution_note}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {alert.status === 'new' && (
                      <Button size="sm" variant="outline" onClick={() => handleAcknowledge(alert.alert_id)}>
                        Acknowledge
                      </Button>
                    )}
                    {alert.status !== 'resolved' && alert.status !== 'dismissed' && (
                      <>
                        <Button size="sm" variant="outline" className="text-green-400 border-green-400 hover:bg-green-400/10" 
                          onClick={() => handleUpdateStatus(alert.alert_id, 'resolved', 'Resolved by admin')}>
                          Resolve
                        </Button>
                        <Button size="sm" variant="ghost" className="text-slate-400"
                          onClick={() => handleUpdateStatus(alert.alert_id, 'dismissed')}>
                          Dismiss
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {total > 20 && (
        <div className="flex justify-center gap-2">
          <Button 
            variant="outline" 
            disabled={page === 1}
            onClick={() => setPage(p => p - 1)}
          >
            Previous
          </Button>
          <span className="flex items-center px-4 text-slate-400">
            Page {page} of {Math.ceil(total / 20)}
          </span>
          <Button 
            variant="outline" 
            disabled={page >= Math.ceil(total / 20)}
            onClick={() => setPage(p => p + 1)}
          >
            Next
          </Button>
        </div>
      )}
    </div>
  );
};

export default CMSAlerts;
